import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert, Divider
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { confirmAcao } from '../services/alerts';

const apiPublica = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api'
});

const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export default function RenovacaoAssinatura() {
    const { token } = useParams();

    const [renovacao, setRenovacao] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [concluido, setConcluido] = useState(null); // 'aceito' | 'recusado'

    useEffect(() => {
        apiPublica.get(`/public/renovacao/${token}`)
            .then(res => setRenovacao(res.data))
            .catch(e => setErro(e.response?.data?.message || 'Link inválido ou expirado. Solicite um novo ao seu psicólogo.'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleAceitar = async () => {
        setEnviando(true);
        try {
            await apiPublica.post(`/public/renovacao/${token}/aceitar`);
            setConcluido('aceito');
        } catch (e) {
            setErro(e.response?.data?.message || 'Erro ao confirmar renovação. Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    const handleRecusar = async () => {
        const ok = await confirmAcao({
            title: 'Encerrar assinatura?',
            text: 'Ao recusar, sua assinatura será encerrada e não serão geradas mais cobranças. '
                + 'Você precisará entrar em contato com seu psicólogo para reativar.',
            confirmButtonText: 'Sim, encerrar',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        setEnviando(true);
        try {
            await apiPublica.post(`/public/renovacao/${token}/recusar`);
            setConcluido('recusado');
        } catch (e) {
            setErro(e.response?.data?.message || 'Erro ao processar recusa. Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    if (erro) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 480, textAlign: 'center' }}>
                    <CancelIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Link inválido</Typography>
                    <Typography color="text.secondary">{erro}</Typography>
                </Paper>
            </Box>
        );
    }

    if (concluido === 'aceito') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 480, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: '#1ABB9C', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Renovação confirmada!</Typography>
                    <Typography color="text.secondary">
                        Sua assinatura foi renovada para {MESES[renovacao.mesReferencia]}/{renovacao.anoReferencia}.
                        Uma cobrança de <strong>R$ {Number(renovacao.valorCobrado).toFixed(2)}</strong> será gerada
                        com vencimento no dia <strong>{renovacao.diaPagamento}</strong>.
                    </Typography>
                    <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: 14 }}>
                        Obrigado pela confiança. Até o próximo mês!
                    </Typography>
                </Paper>
            </Box>
        );
    }

    if (concluido === 'recusado') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 480, textAlign: 'center' }}>
                    <CancelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Assinatura encerrada</Typography>
                    <Typography color="text.secondary">
                        Sua assinatura foi encerrada conforme solicitado. Se mudar de ideia,
                        entre em contato com seu psicólogo.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const jaRespondido = renovacao?.status !== 'PENDENTE';

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, maxWidth: 500, width: '100%' }}>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <AutorenewIcon sx={{ color: '#1ABB9C', fontSize: 36 }} />
                    <Box>
                        <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>Renovação de Assinatura</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {MESES[renovacao.mesReferencia]}/{renovacao.anoReferencia}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2">Paciente</Typography>
                        <Typography fontWeight={600}>{renovacao.nomePaciente}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2">Plano</Typography>
                        <Typography fontWeight={600}>{renovacao.nomePlano}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2">Valor mensal</Typography>
                        <Typography fontWeight={700} color="#1ABB9C" fontSize={18}>
                            R$ {Number(renovacao.valorCobrado).toFixed(2)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2">Vencimento</Typography>
                        <Typography fontWeight={600}>
                            Dia {renovacao.diaPagamento} de {MESES[renovacao.mesReferencia]}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="text.secondary" variant="body2">Link válido até</Typography>
                        <Typography fontWeight={600}>
                            {renovacao.dataExpiracao ? new Date(renovacao.dataExpiracao).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                        </Typography>
                    </Box>
                </Box>

                {jaRespondido ? (
                    <Alert severity={renovacao.status === 'ACEITA' ? 'success' : 'info'}>
                        Esta renovação já foi {renovacao.status === 'ACEITA' ? 'confirmada' : 'recusada'}.
                    </Alert>
                ) : (
                    <>
                        <Alert severity="info" sx={{ mb: 3, fontSize: 13 }}>
                            Ao confirmar, você autoriza a cobrança de <strong>R$ {Number(renovacao.valorCobrado).toFixed(2)}</strong> referente
                            a <strong>{MESES[renovacao.mesReferencia]}/{renovacao.anoReferencia}</strong>.
                        </Alert>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                disabled={enviando}
                                onClick={() => handleRecusar()}
                            >
                                Encerrar assinatura
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={enviando}
                                onClick={handleAceitar}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#159e84' } }}
                            >
                                {enviando ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Renovar'}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>

        </Box>
    );
}
